import React, { useState, useEffect } from 'react';
import { Input, Button, Spin, notification, Form } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import logger from '../../utils/logger';
import { getIntegrations, createIntegration, deleteIntegration } from '../../services/integrations';

interface EmailVerificationIntegrationProps {
  userId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const EmailVerificationIntegration: React.FC<EmailVerificationIntegrationProps> = ({
  userId,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [form] = Form.useForm();


  const checkIntegrationStatus = async () => {
    logger.log("🔍 EMAIL VERIFICATION: Checking integration status...");
    try {
      const { data } = await getIntegrations();
      const emailVerificationIntegration = data?.data?.find(
        (integration: any) => integration.integrationId === "email_verification" ||
        integration.integrationKey === "email_verification"
      );

      logger.log("📋 EMAIL VERIFICATION: Integration found:", emailVerificationIntegration);

      if (emailVerificationIntegration) {
        setIsConnected(true);
        // Check if there's an error message in the integration details
        if (emailVerificationIntegration.details?.errorMessage) {
          setErrorMessage(emailVerificationIntegration.details.errorMessage);
          logger.log("⚠️ EMAIL VERIFICATION: Error message found:", emailVerificationIntegration.details.errorMessage);
        } else {
          setErrorMessage('');
        }
        logger.log("✅ EMAIL VERIFICATION: Integration is connected");
      } else {
        setIsConnected(false);
        setErrorMessage('');
        logger.log("❌ EMAIL VERIFICATION: Integration not connected");
      }
    } catch (error) {
      logger.error('💥 EMAIL VERIFICATION: Error checking status:', error);
      setIsConnected(false);
      setErrorMessage('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const handleConnect = async (values: { apiKey: string }) => {
    logger.log("🔌 EMAIL VERIFICATION: Starting connection process...");
    try {
      setIsSubmitting(true);

      const response = await createIntegration({
        integrationKey: "email_verification",
        details: {
          apiKey: values.apiKey
        }
      });

      logger.log("✅ EMAIL VERIFICATION: Connection response:", response.data);

      if (response.data.status) {
        setIsConnected(true);
        notification.success({
          message: 'Email verification connected successfully!',
          description: 'Your API key has been saved and the integration is now active.'
        });
        onSuccess();
      } else {
        throw new Error(response.data.message || 'Connection failed');
      }
    } catch (error: any) {
      logger.error('💥 EMAIL VERIFICATION: Connection failed:', error);
      notification.error({
        message: 'Connection failed',
        description: error.response?.data?.message || error.message || 'Unable to connect email verification'
      });
      onError('Connection failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    logger.log("🔌 EMAIL VERIFICATION: Starting disconnect process...");
    try {
      setIsSubmitting(true);

      const response = await deleteIntegration("email_verification");

      logger.log("✅ EMAIL VERIFICATION: Disconnect response:", response.data);

      if (response.data.status) {
        setIsConnected(false);
        form.resetFields();
        notification.success({
          message: 'Email verification disconnected successfully!'
        });
        onSuccess();
      } else {
        throw new Error(response.data.message || 'Disconnect failed');
      }
    } catch (error: any) {
      logger.error('💥 EMAIL VERIFICATION: Disconnect failed:', error);
      notification.error({
        message: 'Failed to disconnect',
        description: error.response?.data?.message || error.message || 'Unable to disconnect email verification'
      });
      onError('Disconnect failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '600px',
      margin: '40px auto',
      padding: '30px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      textAlign: 'center'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '30px'
      }}>
        <img
          src="/assets/img/validemail.png"
          alt="ValidEmail"
          style={{
            width: '28px',
            height: '28px',
            marginRight: '15px'
          }}
        />
        <h2 style={{
          margin: 0,
          fontSize: '24px',
          color: '#333'
        }}>
          Email Verification
        </h2>
      </div>

      {loading ? (
        <div>
          <Spin />
          <p style={{ marginTop: '20px', color: '#666' }}>
            Checking Email Verification connection status...
          </p>
        </div>
      ) : isConnected ? (
        <div>
          {errorMessage ? (
            <div style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#fff2e8',
              border: '1px solid #ffbb96',
              borderRadius: '6px',
              color: '#d4380d',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              textAlign: 'left'
            }}>
              <span style={{ fontSize: '18px', lineHeight: '1.4' }}>⚠️</span>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px' }}>Integration Error</strong>
                {errorMessage}
              </div>
            </div>
          ) : (
            <div style={{
              marginBottom: '20px',
              color: '#52c41a',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ color: '#52c41a' }}>✓</span>
              Your Email Verification service is connected and working properly
            </div>
          )}

          <div style={{
            color: '#666',
            fontSize: '15px',
            marginBottom: '20px'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              textAlign: 'left',
              margin: '0 auto',
              maxWidth: '400px'
            }}>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#1890ff', marginRight: '10px' }}>•</span>
                Verify email addresses before sending campaigns
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#1890ff', marginRight: '10px' }}>•</span>
                Reduce bounce rates and improve deliverability
              </li>
              <li style={{ margin: '10px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#1890ff', marginRight: '10px' }}>•</span>
                Maintain sender reputation and avoid blacklisting
              </li>
            </ul>
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Button
              onClick={handleDisconnect}
              loading={isSubmitting}
              type="text"
              style={{
                color: '#1890ff',
                border: 'none',
                padding: '8px 16px'
              }}
            >
              Disconnect Email Verification
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p style={{
            marginBottom: '25px',
            color: '#666',
            fontSize: '15px'
          }}>
            Connect your Email Verification service to validate email addresses, improve deliverability, and reduce bounce rates in your campaigns.
          </p>

          <Form
            form={form}
            onFinish={handleConnect}
            style={{ maxWidth: '400px', margin: '0 auto' }}
          >
            <Form.Item
              name="apiKey"
              rules={[
                { required: true, message: 'Please enter your API key' },
                { min: 10, message: 'API key must be at least 10 characters long' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input.Password
                placeholder="Enter your Email Verification API key"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                style={{
                  padding: '12px',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '0', textAlign: 'center' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                style={{
                  padding: '12px 24px',
                  height: 'auto',
                  fontSize: '15px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                Connect Email Verification
              </Button>
            </Form.Item>
          </Form>

          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f6f8fa',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#666',
            textAlign: 'left'
          }}>
            <strong>Need an API key?</strong>
            <p style={{ margin: '5px 0 10px 0' }}>
              Sign up with ValidEmail.net to get your API key and start verifying email addresses.
            </p>
            <a
              href="https://validemail.net/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#1890ff',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#40a9ff';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#1890ff';
              }}
            >
              Sign up at ValidEmail.net →
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerificationIntegration;